import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { RagBadge, StatusBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, Pencil } from "lucide-react";
import ExportMenu from "@/components/ExportMenu";

const COMMON_UNITS = ["%", "count", "calls", "minutes", "seconds", "₹", "ratio"];

export default function CampaignDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [camp, setCamp] = useState(null);
  const [score, setScore] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [defs, setDefs] = useState([]);
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [trend, setTrend] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [bmKpi, setBmKpi] = useState(null);
  const [editKpi, setEditKpi] = useState(null);

  const canEdit = ["crm_manager", "process_manager", "branch_manager"].includes(user?.role);
  const isAdmin = user?.role === "crm_manager";

  const load = async () => {
    const [c, s, d] = await Promise.all([
      api.get(`/campaigns/${id}`),
      api.get(`/kpis/scorecard/${id}`, { params: { month } }),
      api.get(`/kpis/definitions`, { params: { campaign_id: id } }),
    ]);
    setCamp(c.data); setScore(s.data); setDefs(d.data);
    if (d.data.length) {
      setSelectedKpi((k) => k || d.data[0].id);
    }
  };

  const loadTrend = async (kpiId) => {
    if (!kpiId) return;
    const { data } = await api.get(`/kpis/trend/${kpiId}`, { params: { months: 12 } });
    setTrend(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, month]);
  useEffect(() => { loadTrend(selectedKpi); }, [selectedKpi]);

  if (!camp) return <div className="p-8 text-slate-500">Loading…</div>;

  const recordActual = async (kpi) => {
    const v = window.prompt(`Actual for ${kpi.name} (${kpi.unit}) — ${month}`, kpi.actual ?? "");
    if (v == null || v === "") return;
    try {
      await api.post("/kpis/actuals", {
        kpi_definition_id: kpi.id, month, actual_value: +v,
      });
      toast.success("Recorded");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-5" data-testid="campaign-detail">
      <div className="mptg-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="mptg-overline">{camp.type}</div>
            <h1 className="font-heading text-2xl font-bold tracking-tight mt-1">{camp.name}</h1>
            <div className="text-sm text-slate-500 mt-1">
              Client: <Link to={`/clients/${camp.client_id}`} className="text-[#2E86C1] hover:underline">{camp.client_name}</Link> ·
              Branch: <span className="text-slate-700">{camp.branch || "-"}</span> ·
              PM: <span className="text-slate-700">{camp.process_manager_name || "-"}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={camp.status} />
            <ExportMenu
              endpoint="/kpis/export"
              params={{ campaign_id: id, month }}
              filename={`kpis_${camp.name?.replace(/\s+/g, "_") || id}_${month}`}
              label="Export KPIs"
              testid="kpi-export"
            />
            {isAdmin && (
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-slate-200" data-testid="add-kpi-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add KPI
                  </Button>
                </DialogTrigger>
                <NewKpiDialog campaignId={id} onDone={() => { setCreateOpen(false); load(); }} />
              </Dialog>
            )}
          </div>
        </div>
      </div>

      <div className="mptg-card">
        <div className="flex items-center gap-3 justify-between flex-wrap">
          <div>
            <div className="mptg-overline">KPI scorecard</div>
            <h2 className="font-heading text-lg font-bold">Monthly RAG status</h2>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="month-picker" />
          </div>
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full mptg-table">
            <thead><tr><th>KPI</th><th>Unit</th><th>Direction</th><th className="text-right">Benchmark</th><th className="text-right">Actual</th><th className="text-right">Variance</th><th>RAG</th>{canEdit && <th></th>}</tr></thead>
            <tbody>
              {score.length === 0 ? (
                <tr><td colSpan={canEdit ? 8 : 7} className="py-8 text-center text-slate-400">No KPIs defined yet.</td></tr>
              ) : score.map((k) => (
                <tr key={k.id} className="cursor-pointer" onClick={() => setSelectedKpi(k.id)}>
                  <td className="font-medium">{k.name}</td>
                  <td className="text-slate-600">{k.unit}</td>
                  <td className="text-xs text-slate-500">{k.direction === "higher_is_better" ? "↑" : "↓"}</td>
                  <td className="text-right tabular-nums">{k.benchmark ?? "-"}</td>
                  <td className="text-right tabular-nums">{k.actual ?? "-"}</td>
                  <td className="text-right tabular-nums">{k.variance != null ? `${k.variance}%` : "-"}</td>
                  <td><RagBadge status={k.status} /></td>
                  {canEdit && (
                    <td className="text-right whitespace-nowrap space-x-3">
                      <button
                        className="text-xs text-[#2E86C1] hover:underline"
                        onClick={(e) => { e.stopPropagation(); recordActual(k); }}
                        data-testid={`record-actual-${k.id}`}
                      >
                        {k.actual != null ? "Update" : "Record"}
                      </button>
                      {isAdmin && (
                        <>
                          <button
                            className="text-xs text-slate-600 hover:underline"
                            onClick={(e) => { e.stopPropagation(); setBmKpi(k); }}
                            data-testid={`update-benchmark-${k.id}`}
                          >
                            Set benchmark
                          </button>
                          <button
                            className="text-xs text-[#2E86C1] hover:underline inline-flex items-center gap-1"
                            onClick={(e) => { e.stopPropagation(); setEditKpi(k); }}
                            data-testid={`edit-kpi-${k.id}`}
                          >
                            <Pencil className="w-3 h-3" /> Edit
                          </button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mptg-card">
        <div className="mptg-overline">KPI trend (last 12 months)</div>
        <div className="mt-2 flex items-center gap-2">
          <Select value={selectedKpi || ""} onValueChange={setSelectedKpi}>
            <SelectTrigger className="w-72" data-testid="trend-kpi-select"><SelectValue placeholder="Pick a KPI" /></SelectTrigger>
            <SelectContent>
              {defs.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="h-64 mt-3">
          {trend.length === 0 ? (
            <div className="h-full flex items-center justify-center text-slate-400 text-sm">No actuals for this KPI.</div>
          ) : (
            <ResponsiveContainer>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip contentStyle={{ background: "#1B2A4A", border: 0, color: "#fff", fontSize: 12 }} />
                <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
                <Line name="Actual" dataKey="actual_value" stroke="#2E86C1" strokeWidth={2} dot={{ r: 3 }} />
                <Line name="Benchmark" dataKey="benchmark_value_at_time" stroke="#94A3B8" strokeDasharray="4 4" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {bmKpi && (
        <BenchmarkDialog kpi={bmKpi} onClose={() => setBmKpi(null)} onDone={() => { setBmKpi(null); load(); }} />
      )}

      {editKpi && (
        <EditKpiDialog kpi={editKpi} onClose={() => setEditKpi(null)} onDone={() => { setEditKpi(null); load(); }} />
      )}
    </div>
  );
}

function BenchmarkDialog({ kpi, onClose, onDone }) {
  const [value, setValue] = useState(kpi.benchmark ?? 0);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/kpis/definitions/${kpi.id}/benchmark`, {
        benchmark_value: +value, effective_from: date, reason_for_change: reason,
      });
      toast.success("Benchmark updated (history preserved)");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="benchmark-dialog">
        <DialogHeader><DialogTitle>Update benchmark — {kpi.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-xs text-slate-500">
            Current benchmark: <strong>{kpi.benchmark ?? "-"} {kpi.unit}</strong>.
            New value will apply from the effective date — the old benchmark stays in history.
          </div>
          <div><Label>New benchmark value</Label>
            <Input type="number" value={value} onChange={(e) => setValue(e.target.value)} data-testid="benchmark-value" /></div>
          <div><Label>Effective from</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div><Label>Reason for change</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. revised SLA 2026-Q2" /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="save-benchmark-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewKpiDialog({ campaignId, onDone }) {
  const [f, setF] = useState({
    name: "", unit: "%", direction: "higher_is_better", benchmark_value: 0,
    effective_from: new Date().toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await api.post("/kpis/definitions", { campaign_id: campaignId, ...f });
      toast.success("KPI added"); onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>New KPI</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-3 py-2">
        <div className="col-span-2"><Label>Name</Label>
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="kpi-name-input" /></div>
        <div><Label>Unit</Label>
          <Select value={COMMON_UNITS.includes(f.unit) ? f.unit : "__other"} onValueChange={(v) => setF({ ...f, unit: v === "__other" ? "" : v })}>
            <SelectTrigger data-testid="kpi-unit-select"><SelectValue placeholder="Pick unit" /></SelectTrigger>
            <SelectContent>
              {COMMON_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              <SelectItem value="__other">Other (type below)</SelectItem>
            </SelectContent>
          </Select>
          {!COMMON_UNITS.includes(f.unit) && (
            <Input className="mt-2" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} placeholder="Custom unit (e.g., sec, calls)" data-testid="kpi-unit-custom" />
          )}
        </div>
        <div><Label>Direction</Label>
          <Select value={f.direction} onValueChange={(v) => setF({ ...f, direction: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="higher_is_better">Higher is better</SelectItem>
              <SelectItem value="lower_is_better">Lower is better</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Benchmark</Label>
          <Input type="number" value={f.benchmark_value} onChange={(e) => setF({ ...f, benchmark_value: +e.target.value })} /></div>
        <div><Label>Effective from</Label>
          <Input type="date" value={f.effective_from} onChange={(e) => setF({ ...f, effective_from: e.target.value })} /></div>
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={saving} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="save-kpi-btn">Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function EditKpiDialog({ kpi, onClose, onDone }) {
  const [f, setF] = useState({
    name: kpi.name || "",
    unit: kpi.unit || "%",
    direction: kpi.direction || "higher_is_better",
    is_active: kpi.is_active !== false,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/kpis/definitions/${kpi.id}`, f);
      toast.success("KPI updated"); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-kpi-dialog">
        <DialogHeader><DialogTitle>Edit KPI — {kpi.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2"><Label>Name</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="edit-kpi-name" /></div>
          <div><Label>Unit</Label>
            <Select value={COMMON_UNITS.includes(f.unit) ? f.unit : "__other"} onValueChange={(v) => setF({ ...f, unit: v === "__other" ? "" : v })}>
              <SelectTrigger data-testid="edit-kpi-unit"><SelectValue /></SelectTrigger>
              <SelectContent>
                {COMMON_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                <SelectItem value="__other">Other (type below)</SelectItem>
              </SelectContent>
            </Select>
            {!COMMON_UNITS.includes(f.unit) && (
              <Input className="mt-2" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} placeholder="Custom unit" />
            )}
          </div>
          <div><Label>Direction</Label>
            <Select value={f.direction} onValueChange={(v) => setF({ ...f, direction: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="higher_is_better">Higher is better</SelectItem>
                <SelectItem value="lower_is_better">Lower is better</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 flex items-center gap-2 pt-2">
            <input type="checkbox" id="kpi-active" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} data-testid="edit-kpi-active" />
            <label htmlFor="kpi-active" className="text-sm">Active (unchecking will hide this KPI from new scorecards)</label>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="save-edit-kpi">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

